<script setup lang="ts">
import { resetForm } from '@/helpers/ep/form'
import type { IRepairActionForm } from '@/types/rc/forms'
import { type FormInstance, type FormRules } from 'element-plus'
import { computed, reactive, ref } from 'vue'
import type { IRepairAction } from '@/types/rc/miners'
import { PartType } from '@/types/rc/miners/PartType'
import { useRepairActions } from '@/composables/rc/repair-actions'

const { createRepairActionMutation, updateRepairActionMutation } = useRepairActions()
const {
  isPending: createIsPending,
  mutateAsync: createAsync,
  isError: createIsError,
  error: createError,
} = createRepairActionMutation

const {
  isPending: updateIsPending,
  mutateAsync: updateAsync,
  isError: updateIsError,
  error: updateError,
} = updateRepairActionMutation

const open = defineModel<boolean>()
const props = defineProps<{ repairActionToUpdate?: IRepairAction }>()
const emit = defineEmits(['closed'])

const repairActionFormRef = ref<FormInstance>()
const repairActionForm = reactive<IRepairActionForm>({
  part_type: '',
  action: '',
  price: undefined,
})

const rules = reactive<FormRules<IRepairActionForm>>({
  part_type: [{ required: true, message: 'Part type is required.', trigger: 'blur' }],
  action: [{ required: true, message: 'Action name is required.', trigger: 'blur' }],
  price: [{ required: true, message: 'Price is required.', trigger: 'blur' }],
})

/**
 * Return form loading state (used to disable form).
 */
const formLoading = computed<boolean>(() => {
  return createIsPending.value || updateIsPending.value
})

/**
 * Handles form submit.
 * Create or updates a repair action, depending on the form mode.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  formEl?.validate(async (valid) => {
    if (!valid) {
      return
    }

    try {
      if (props.repairActionToUpdate) {
        await updateAsync({ repairActionID: props.repairActionToUpdate.id, form: repairActionForm })
        open.value = false
      } else {
        await createAsync({ ...repairActionForm })
        open.value = false
      }
    } catch {
      // handled in UI
    }
  })
}

/**
 * Reset form
 */
const handleClosed = () => {
  resetForm(repairActionFormRef.value)
  createRepairActionMutation.reset()
  updateRepairActionMutation.reset()
  emit('closed')
}

const initForm = () => {
  if (props.repairActionToUpdate) {
    Object.assign(repairActionForm, props.repairActionToUpdate)
  }
}
</script>

<template>
  <el-dialog
    v-model="open"
    :width="600"
    :title="`${repairActionToUpdate ? 'Update' : 'Create'} Repair Charge`"
    :show-close="false"
    @closed="handleClosed"
    @open="initForm"
  >
    <el-form
      ref="repairActionFormRef"
      :model="repairActionForm"
      :rules="rules"
      :label-width="160"
      label-position="left"
      :disabled="formLoading"
    >
      <el-form-item label="Part Type" prop="part_type">
        <el-select v-model="repairActionForm.part_type" clearable placeholder="Part Type">
          <el-option v-for="item in PartType" :key="item" :label="item" :value="item" />
        </el-select>
      </el-form-item>
      <el-form-item label="Description" prop="action">
        <el-input v-model="repairActionForm.action" placeholder="Description" clearable />
      </el-form-item>
      <el-form-item label="Price" prop="price">
        <el-currency-input v-model="repairActionForm.price" clearable />
      </el-form-item>
    </el-form>
    <rl-error-alert v-if="createIsError" :error="createError" />
    <rl-error-alert v-if="updateIsError" :error="updateError" />
    <template #footer>
      <el-button :disabled="formLoading || !open" @click="open = false">Cancel</el-button>
      <el-button
        type="primary"
        :loading="createIsPending || updateIsPending"
        :disabled="formLoading || !open"
        @click="submitForm(repairActionFormRef)"
        >Submit</el-button
      >
    </template>
  </el-dialog>
</template>
